import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Search, Plus, Edit, Trash2, Archive, CheckCircle } from 'lucide-react';
import { BillingItem } from '../../types';
export const ItemList: React.FC = () => {
  const {
    billingItems,
    addBillingItem,
    updateBillingItem,
    deleteBillingItem,
    currentUser,
    logBillingAudit
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BillingItem | null>(null);
  const [formData, setFormData] = useState({
    itemCode: '',
    serviceName: '',
    category: 'Consultation',
    department: 'General',
    description: '',
    unitPrice: '',
    taxRate: '0',
    discountEligibility: true,
    status: 'Active' as 'Active' | 'Inactive' | 'Archived'
  });
  const isAdmin = currentUser?.role === 'Administrator';
  const filteredItems = billingItems.filter(i => i.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) || i.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) || i.category.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleOpenModal = (item?: BillingItem) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        itemCode: item.itemCode,
        serviceName: item.serviceName,
        category: item.category,
        department: item.department,
        description: item.description || '',
        unitPrice: item.unitPrice.toString(),
        taxRate: item.taxRate.toString(),
        discountEligibility: item.discountEligibility,
        status: item.status
      });
    } else {
      setEditingItem(null);
      setFormData({
        itemCode: '',
        serviceName: '',
        category: 'Consultation',
        department: 'General',
        description: '',
        unitPrice: '',
        taxRate: '0',
        discountEligibility: true,
        status: 'Active'
      });
    }
    setShowModal(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      itemCode: formData.itemCode,
      serviceName: formData.serviceName,
      category: formData.category,
      department: formData.department,
      description: formData.description,
      unitPrice: parseFloat(formData.unitPrice) || 0,
      taxRate: parseFloat(formData.taxRate) || 0,
      discountEligibility: formData.discountEligibility,
      status: formData.status
    };
    if (editingItem) {
      updateBillingItem(editingItem.id, payload);
      logBillingAudit('Update Item', `Updated billing item ${payload.itemCode}`);
    } else {
      addBillingItem({
        ...payload,
        createdAt: new Date().toISOString()
      });
      logBillingAudit('Create Item', `Created billing item ${payload.itemCode}`);
    }
    setShowModal(false);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const item = billingItems.find(i => i.id === id);
      deleteBillingItem(id);
      if (item) {
        logBillingAudit('Delete Item', `Deleted billing item ${item.itemCode}`);
      }
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600" data-id="element-1372" data-mp="a8hgo1qwfjs7">
        Only Administrators can manage the Item List.
      </div>;
  }
  return <div className="space-y-6" data-id="element-1373" data-mp="68fcnlxoye9r">
      <div className="flex justify-between items-center" data-id="element-1374" data-mp="698lqtk4hfv2">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1375" data-mp="k7yiu96xzfjf">
          Item List (Service Catalogue)
        </h1>
        <Button onClick={() => handleOpenModal()} data-id="element-1376" data-mp="f9530a46ncg3">
          <Plus className="w-4 h-4 mr-2" data-id="element-1377" data-mp="z9x1scbrchnb" /> Add Item
        </Button>
      </div>

      <Card noPadding data-id="element-1378" data-mp="z8425gzkaxot">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1379" data-mp="13ba78laxuitp">
          <div className="relative max-w-md" data-id="element-1380" data-mp="139fj6y4deale">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1381" data-mp="181yvlrrbmzom">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1382" data-mp="1d1l8g038mdww" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name, code, or category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1383" data-mp="gb2lv00k5b5k" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1384" data-mp="20qo102zetr66">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1385" data-mp="25pqvwuloeh0f">
            <thead className="bg-gray-50" data-id="element-1386" data-mp="25s3khqlxvpmg">
              <tr data-id="element-1387" data-mp="3q8b4o9ob0c7a">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1388" data-mp="3q6qa4k1zot9f">
                  Item Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1389" data-mp="3ghymgo3yov4b">
                  Service Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1390" data-mp="3bivjd97jkyyz">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1391" data-mp="32i7aesx09osy">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1392" data-mp="2skatfkhdzzmx">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1393" data-mp="vrhdz5t81lnz">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1394" data-mp="2l7jysk55yc72">
              {filteredItems.length > 0 ? filteredItems.map(item => <tr key={item.id} className="hover:bg-gray-50" data-id="element-1395" data-mp="2bbgvvc2hm9j2">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1396" data-mp="2b944y9er4otv">
                      {item.itemCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1397" data-mp="340xkdymzl6a0">
                      {item.serviceName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1398" data-mp="2uyy1f0sevs4f">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1399" data-mp="2yyyhepv5sbdl">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1400" data-mp="1nm5srnxo031r">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Active' ? 'bg-green-100 text-green-800' : item.status === 'Inactive' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1401" data-mp="1g128l97lpsmc">
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1402" data-mp="xau9r3axpoby">
                      <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:text-blue-900 mr-3" title="Edit" data-id="element-1403" data-mp="sciwsjpcexpi">
                        <Edit className="w-4 h-4" data-id="element-1404" data-mp="129f8szega1yt" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-1405" data-mp="ek2tdhsj4xo2">
                        <Trash2 className="w-4 h-4" data-id="element-1406" data-mp="3k95nrknn02ko" />
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1407" data-mp="3u5ihgij6mgff">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1408" data-mp="3u4z2naotw85m">
                    No items found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Item Modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1409" data-mp="gr5bvueynd1s">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" data-id="element-1410" data-mp="lpyug193tu8t">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1411" data-mp="ls1fe6vegx6o">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1412" data-mp="uq55lghbyi4t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1413" data-mp="2f8xs0pnwnmnx">
                <Input label="Item Code" required value={formData.itemCode} onChange={e => setFormData({
              ...formData,
              itemCode: e.target.value
            })} placeholder="e.g., CONS-001" data-id="element-1414" data-mp="2fa8umm5m6odg" />
                <Input label="Service Name" required value={formData.serviceName} onChange={e => setFormData({
              ...formData,
              serviceName: e.target.value
            })} data-id="element-1415" data-mp="2xyxd71gmkhm2" />
                <Select label="Category" required value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} options={[{
              value: 'Consultation',
              label: 'Consultation'
            }, {
              value: 'Laboratory',
              label: 'Laboratory'
            }, {
              value: 'Radiology',
              label: 'Radiology'
            }, {
              value: 'Pharmacy',
              label: 'Pharmacy'
            }, {
              value: 'Procedures',
              label: 'Procedures'
            }, {
              value: 'Theatre',
              label: 'Theatre'
            }, {
              value: 'Maternity',
              label: 'Maternity'
            }, {
              value: 'Medical Reports',
              label: 'Medical Reports'
            }, {
              value: 'Occupational Health',
              label: 'Occupational Health'
            }, {
              value: 'Corporate Services',
              label: 'Corporate Services'
            }, {
              value: 'Administrative Charges',
              label: 'Administrative Charges'
            }, {
              value: 'Miscellaneous Services',
              label: 'Miscellaneous Services'
            }]} data-id="element-1416" data-mp="3hnxz0gs7gb97" />
                <Input label="Department" required value={formData.department} onChange={e => setFormData({
              ...formData,
              department: e.target.value
            })} data-id="element-1417" data-mp="tz2ijvn4e1a4" />
                <Input label="Unit Price (ZMW)" type="number" step="0.01" required value={formData.unitPrice} onChange={e => setFormData({
              ...formData,
              unitPrice: e.target.value
            })} data-id="element-1418" data-mp="2qwqdy1f9f8qz" />
                <Input label="Tax Rate (%)" type="number" step="0.1" required value={formData.taxRate} onChange={e => setFormData({
              ...formData,
              taxRate: e.target.value
            })} data-id="element-1419" data-mp="3ak7ne18b1bhm" />
                <Select label="Status" required value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as any
            })} options={[{
              value: 'Active',
              label: 'Active'
            }, {
              value: 'Inactive',
              label: 'Inactive'
            }, {
              value: 'Archived',
              label: 'Archived'
            }]} data-id="element-1420" data-mp="9phsm5joqii3" />
                <div className="flex items-center mt-6" data-id="element-1421" data-mp="3450ed69l5v8p">
                  <input id="discountEligibility" type="checkbox" checked={formData.discountEligibility} onChange={e => setFormData({
                ...formData,
                discountEligibility: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1422" data-mp="346bl9eybvj41" />
                  <label htmlFor="discountEligibility" className="ml-2 block text-sm text-gray-900" data-id="element-1423" data-mp="2hw1q973coduk">
                    Eligible for Discount
                  </label>
                </div>
              </div>
              <div className="mt-4" data-id="element-1424" data-mp="23wivdfzjuz07">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1425" data-mp="23xk7lnsaxrji">
                  Description (Optional)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-1426" data-mp="1ben353mrn3lp" />
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-1427" data-mp="3d4c3095u0xi1">
                <Button variant="outline" type="button" onClick={() => setShowModal(false)} data-id="element-1428" data-mp="3d4vofzuoy40t">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-1429" data-mp="9lj32z5z876c">
                  {editingItem ? 'Update Item' : 'Save Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};